import type { ApiReturnProject } from '@highimpact-frontend/api-types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import ListDataGrid from '../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import { useCheckPermission } from '../../../components/utils/permission/helpers/usePermission';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import { resetExternalState } from '../../../store/data/slice';
import { useAppDispatch } from '../../../store/hooks';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import { useTranslation } from 'react-i18next';
import ProjectService from '../../../api/services/config/project/ProjectService';
import CreateProjectForm from '../../../components/projects/CreateProjectForm';
import EditProjectForm from '../../../components/projects/EditProjectForm';
import CreateRessource from '../../../components/ressources/CreateRessource';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import { getOrElse } from '../../../utils/functions';
import { useModal } from '../../../utils/hooks/useModal';

type NewRow<T extends { id: string | number }> = Partial<Omit<T, 'id'>> & {
  id: T['id'];
  project: T;
};

interface FilteredProjectsProps {
  projects: ApiReturnProject[];
  refresh: () => void;
}

const FilteredProjects: React.FC<FilteredProjectsProps> = ({ projects }) => {
  // Add translation here :
  const { t } = useTranslation('components.projects');

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const createModal = useModal();
  const editModal = useModal<{ project: ApiReturnProject | null }>();
  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    projectId: number;
  }>();
  const addResourceModal = useModal<{ project_id: number | null }>();
  const { shouldHide: hideEditButton, canDisable: disableEditButton } =
    useCheckPermission('configuration_project_edit_button');
  const { shouldHide: hideDeleteButton, canDisable: disableDeleteButton } =
    useCheckPermission('configuration_project_delete_button');

  /**
   * Delete a specific project.
   * @param id
   */
  const deleteProject = async (id: number) => {
    submit({
      callbacks: [
        async () => await ProjectService.delete(id),
        () => dispatch(resetExternalState({ external_key: 'projects' })),
        () => dispatch(resetExternalState({ external_key: 'project_sites' })),
      ],
      errorMessages: {
        anyError: t('Une erreur est survenue lors de la suppression du projet'),
        unicityError: 'Vous avez déjà supprimé ce projet',
      },
      successMessage: t('Le projet a été supprimé avec succès'),
    });
  };

  /**
   * Pretty names for headers.
   */
  const headingsPrettyNames: Partial<Record<keyof ApiReturnProject, string>> = {
    name: t('Nom'),
  };

  /**
   * For each project, creating mui row structure.
   * @param project
   * @returns
   */
  const newCreateRow = (
    project: ApiReturnProject
  ): NewRow<ApiReturnProject> => ({
    id: Number.parseInt(getOrElse(project.id)),
    name: getOrElse(project.name),
    start_date: project.start_date,
    end_date: project.end_date,
    number: getOrElse(project.number),
    project,
  });

  /**
   * For each key on ApiReturnProject, create mui column structure.
   * @param field
   * @returns
   */
  const createColumn = (
    field: keyof ApiReturnProject
  ): GridColDef<NewRow<ApiReturnProject>> => ({
    field,
    headerName: headingsPrettyNames[field],
    headerClassName: 'list-data-grid-style_table-header',
    flex: 1,
    minWidth: 100,
    sortable: true,
    filterable: true,
  });

  // Defining columns for each key + actions column.
  const columns: GridColDef<NewRow<ApiReturnProject>>[] = [
    ...(Object.keys(headingsPrettyNames) as (keyof ApiReturnProject)[]).map(
      (key) => createColumn(key)
    ),
    {
      field: 'start_date',
      headerName: t('Date de début'),
      flex: 1,
      minWidth: 100,
      sortable: true,
      filterable: true,
      headerClassName: 'list-data-grid-style_table-header',
      //for correct sort and filter the dates, documentation mui v7+
      valueGetter: (value) => {
        if (!value) {
          return null;
        }
        const dateToFilter = new Date(value).getTime();
        return dateToFilter;
      },
      valueFormatter: (value) => {
        if (!value) {
          return '--';
        }
        const dateToShow = getOrElse(
          new Date(value).toLocaleDateString('en-UK').replace(/\//g, '-')
        );
        return dateToShow;
      },
    },
    {
      field: 'end_date',
      headerName: t('Date de fin'),
      flex: 1,
      minWidth: 100,
      sortable: true,
      filterable: true,
      headerClassName: 'list-data-grid-style_table-header',
      valueGetter: (value) => {
        if (!value) {
          return null;
        }
        const dateToFilter = new Date(value).getTime();
        return dateToFilter;
      },
      valueFormatter: (value) => {
        if (!value) {
          return '--';
        }
        const dateToShow = getOrElse(
          new Date(value).toLocaleDateString('en-UK').replace(/\//g, '-')
        );
        return dateToShow;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      headerClassName: 'list-data-grid-style_table-header',
      minWidth: 100,
      getActions: (params) =>
        [
          !hideEditButton && (
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label={t('Modifier')}
              data-testid={`edit-project-${params.row.name}`}
              onClick={() => editModal.open({ project: params.row.project })}
              showInMenu
              disabled={disableEditButton}
            />
          ),
          !hideDeleteButton && (
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              data-testid={`delete-project-${params.row.name}`}
              label={t('Supprimer')}
              onClick={() =>
                deleteModal.open({
                  callback: () => deleteProject(params.id as number),
                  message: t('Voulez-vous supprimer le projet'),
                  target: params.row.name as string,
                  projectId: params.id as number,
                })
              }
              showInMenu
              disabled={disableDeleteButton}
            />
          ),
          // <GridActionsCellItem
          //   key="add"
          //   icon={<AddIcon />}
          //   label="Ajouter une resource"
          //   onClick={() => editModal.open({ project: params.row.project })}
          //   showInMenu
          // />,
        ].filter(Boolean) as React.ReactElement[],
    },
  ];

  // Defining rows.
  const rows: NewRow<ApiReturnProject>[] = projects.map(newCreateRow).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2 item={'configuration_project_create_button'}>
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
            data-testid="create-project"
          >
            {t('Créer un projet')}
          </button>
        </PermissionDecoratorV2>
      </div>
      <ListDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection={false}
        height="calc(100vh - 80px - 125px)"
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
      />
      {deleteModal.isOpen && (
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback()}
          open={deleteModal.isOpen}
          title={'Confirmation'}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      )}
      {createModal.isOpen && (
        <CreateProjectForm
          title={t('Créer un nouveau projet')}
          onCancel={createModal.close}
          open={createModal.isOpen}
        />
      )}
      {addResourceModal.isOpen && (
        <CreateRessource
          handleClose={addResourceModal.close}
          isModalOpen={addResourceModal.isOpen}
          project_id={addResourceModal.params!.project_id as number}
        />
      )}
      <EditProjectForm
        project={editModal.params?.project!}
        isModalOpen={editModal.isOpen}
        handleClose={editModal.close}
      />
    </div>
  );
};

export default FilteredProjects;
